import { useEffect } from "react";
import type { RefObject } from "react";
import type { HomeSectionId } from "../content/siteContent";
import { useAppStore } from "../store/useAppStore";
import { useSectionEntryStore } from "../store/useSectionEntryStore";

type ObservedSection = {
  id: HomeSectionId;
  ref: RefObject<HTMLElement>;
};

const projectsToTeamTriggerProgress = 0.72;
const scrollVelocityEpsilon = 0.02;

export function useHomeSectionObserver(sections: ObservedSection[]) {
  const setTargetSection = useAppStore((state) => state.setTargetSection);
  const setAppScrollVelocity = useAppStore((state) => state.setScrollVelocity);
  const navigationIntent = useAppStore((state) => state.navigationIntent);
  const clearNavigationIntent = useAppStore((state) => state.clearNavigationIntent);
  const experienceMode = useAppStore((state) => state.experienceMode);
  const currentSection = useAppStore((state) => state.currentSection);
  const isTransitioning = useAppStore((state) => state.isTransitioning);

  useEffect(() => {
    if (experienceMode !== "home") {
      return;
    }

    let frame = 0;
    let previousScrollY = window.scrollY;
    let previousTime = performance.now();

    const measureProgress = (node: HTMLElement) => {
      const rect = node.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const navOffsetValue = window.getComputedStyle(document.documentElement).getPropertyValue("--site-nav-offset");
      const navOffset = Number.parseFloat(navOffsetValue) || 0;
      const stickyTop = navOffset + 24;
      const stickyHeight = viewportHeight - stickyTop;
      const travelDistance = Math.max(rect.height - stickyHeight, 1);
      return Math.min(Math.max((stickyTop - rect.top) / travelDistance, 0), 1);
    };

    const measureSection = () => {
      const now = performance.now();
      const scrollY = window.scrollY;
      const deltaTime = Math.max(now - previousTime, 1);
      const velocity = (scrollY - previousScrollY) / deltaTime;
      previousScrollY = scrollY;
      previousTime = now;

      const entryStore = useSectionEntryStore.getState();
      setAppScrollVelocity(velocity);
      entryStore.setScrollVelocity(velocity);

      const currentNode = sections.find((section) => section.id === currentSection)?.ref.current;
      let currentProgress = 0;
      if (currentNode) {
        currentProgress = measureProgress(currentNode);
        entryStore.updateActiveSectionProgress(currentSection, currentProgress, velocity, now, isTransitioning);
      }

      if (isTransitioning) {
        return;
      }

      if (navigationIntent) {
        const targetNode = sections.find((section) => section.id === navigationIntent.target)?.ref.current;
        if (!targetNode) {
          clearNavigationIntent();
          return;
        }

        if (currentSection !== navigationIntent.target) {
          const targetIndex = sections.findIndex((section) => section.id === navigationIntent.target);
          const currentIndex = sections.findIndex((section) => section.id === currentSection);
          const direction = targetIndex < currentIndex ? "backward" : "forward";
          const targetProgress = measureProgress(targetNode);
          useSectionEntryStore.getState().activateSection(navigationIntent.target, now, targetProgress, direction);
          setTargetSection(navigationIntent.target);
          return;
        }

        const targetRect = targetNode.getBoundingClientRect();
        const navOffsetValue = window.getComputedStyle(document.documentElement).getPropertyValue("--site-nav-offset");
        const navOffset = Number.parseFloat(navOffsetValue) || 0;
        const targetTop = navigationIntent.target === "studio" ? 0 : navOffset + 12;
        const hasReachedTargetShell = Math.abs(targetRect.top - targetTop) <= 56;
        if (hasReachedTargetShell) {
          clearNavigationIntent();
        }

        return;
      }

      if (currentSection === "projects" && currentNode) {
        const projectsEntryState = useSectionEntryStore.getState().sections.projects;
        const shouldTriggerProjectsToTeam =
          velocity > scrollVelocityEpsilon &&
          projectsEntryState.canAdvance &&
          !projectsEntryState.isEntryLocked &&
          currentProgress >= projectsToTeamTriggerProgress;

        if (shouldTriggerProjectsToTeam) {
          useSectionEntryStore.getState().activateSection("team", now, 0, "forward");
          setTargetSection("team");
          return;
        }
      }

      const viewportHeight = window.innerHeight;
      const navOffsetValue = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue("--site-nav-offset");
      const navOffset = Number.parseFloat(navOffsetValue) || 0;
      const handoffThreshold = Math.min(Math.max(navOffset + 48, viewportHeight * 0.18), viewportHeight * 0.28);
      let resolvedIndex = 0;

      sections.forEach((section, index) => {
        const node = section.ref.current;
        if (!node) {
          return;
        }

        const rect = node.getBoundingClientRect();
        if (rect.top <= handoffThreshold) {
          resolvedIndex = index;
        }
      });

      const candidate = sections[resolvedIndex]?.id;
      const currentIndex = sections.findIndex((section) => section.id === currentSection);
      const candidateIndex = sections.findIndex((section) => section.id === candidate);
      const currentEntryState = useSectionEntryStore.getState().sections[currentSection];

      const movingForward = candidateIndex > currentIndex && velocity >= 0;
      const movingBackward = candidateIndex < currentIndex && velocity < -scrollVelocityEpsilon;

      if (
        candidate &&
        candidate !== currentSection &&
        currentEntryState.isEntryLocked &&
        candidateIndex !== -1 &&
        currentIndex !== -1 &&
        movingForward
      ) {
        return;
      }

      if (candidate && candidate !== currentSection && candidateIndex < currentIndex && !movingBackward) {
        return;
      }

      if (candidate && candidate !== currentSection) {
        const candidateNode = sections[resolvedIndex]?.ref.current;
        const candidateProgress = candidateNode ? measureProgress(candidateNode) : 0;
        useSectionEntryStore
          .getState()
          .activateSection(candidate, now, candidateProgress, candidateIndex < currentIndex ? "backward" : "forward");
        setTargetSection(candidate);
      }
    };

    const requestMeasure = () => {
      if (frame) {
        return;
      }

      frame = window.requestAnimationFrame(() => {
        frame = 0;
        measureSection();
      });
    };

    requestMeasure();
    window.addEventListener("scroll", requestMeasure, { passive: true });
    window.addEventListener("resize", requestMeasure);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener("scroll", requestMeasure);
      window.removeEventListener("resize", requestMeasure);
    };
  }, [
    clearNavigationIntent,
    currentSection,
    experienceMode,
    isTransitioning,
    navigationIntent,
    sections,
    setAppScrollVelocity,
    setTargetSection
  ]);

  useEffect(() => {
    if (experienceMode !== "home") {
      return;
    }

    const entryStore = useSectionEntryStore.getState();
    const existingState = entryStore.sections[currentSection];
    if (entryStore.activeSection === currentSection && existingState.activatedAt !== null && existingState.phase !== "exit") {
      return;
    }

    const node = sections.find((section) => section.id === currentSection)?.ref.current;
    const currentProgress = node
      ? (() => {
          const rect = node.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const navOffsetValue = window.getComputedStyle(document.documentElement).getPropertyValue("--site-nav-offset");
          const navOffset = Number.parseFloat(navOffsetValue) || 0;
          const stickyTop = navOffset + 24;
          const stickyHeight = viewportHeight - stickyTop;
          const travelDistance = Math.max(rect.height - stickyHeight, 1);
          return Math.min(Math.max((stickyTop - rect.top) / travelDistance, 0), 1);
        })()
      : 0;

    useSectionEntryStore.getState().activateSection(currentSection, performance.now(), currentProgress, existingState.direction);
  }, [currentSection, experienceMode]);

  useEffect(() => {
    if (experienceMode !== "home" || !isTransitioning) {
      return;
    }

    useSectionEntryStore.getState().markSectionExit(currentSection);
  }, [currentSection, experienceMode, isTransitioning]);
}
